import React, { useState, useEffect, useMemo } from 'react';
import { ordersAPI, locationsAPI } from '@/lib/api';
import { getToday, formatDate } from '@/lib/utils';
import { Calendar, TrendingUp, AlertCircle, Filter, CheckSquare, Square, MapPin, ChevronDown, ChevronUp, AlertTriangle, Copy, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const AdminConsolidatedPage = () => {
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getToday());
  const [useRange, setUseRange] = useState(false);
  const [consolidated, setConsolidated] = useState(null);
  const [ordersByDate, setOrdersByDate] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState({});
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [selectedOrderType, setSelectedOrderType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showOrderSelector, setShowOrderSelector] = useState(false);

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    if (!useRange) {
      loadOrdersForDate();
    }
  }, [selectedDate]);

  const loadLocations = async () => {
    try {
      const response = await locationsAPI.getAll(true);
      setLocations(response.data);
      // Inicializar todos los locales como seleccionados
      const initialSelection = {};
      response.data.forEach(loc => {
        initialSelection[loc.id] = true;
      });
      setSelectedLocations(initialSelection);
      
      // Detectar locales sin bodega asignada
      const sinBodega = response.data.filter(loc => !loc.warehouse_id);
      if (sinBodega.length > 0) {
        console.warn('Locales sin bodega asignada:', sinBodega.map(l => l.name));
      }
    } catch (error) {
      console.error('Error al cargar locales:', error);
    }
  };

  // Detectar locales sin bodega
  const localesSinBodega = useMemo(() => {
    return locations.filter(loc => !loc.warehouse_id);
  }, [locations]);

  const loadOrdersForDate = async () => {
    try {
      const response = await ordersAPI.getByDate(selectedDate);
      setOrdersByDate(response.data);
    } catch (error) {
      setOrdersByDate([]);
    }
  };

  const loadConsolidated = async () => {
    setLoading(true);
    try {
      // Si usamos rango de fechas, necesitamos cargar múltiples días
      if (useRange) {
        await loadConsolidatedRange();
      } else {
        const orderType = selectedOrderType !== 'all' ? selectedOrderType : null;
        const response = await ordersAPI.getConsolidated(selectedDate, null, orderType);
        // Filtrar por locales seleccionados
        const filteredItems = filterBySelectedLocations(response.data.items);
        setConsolidated({
          ...response.data,
          items: filteredItems
        });
      }
    } catch (error) {
      toast.error('Error al cargar consolidado');
      setConsolidated(null);
    } finally {
      setLoading(false);
    }
  };

  const loadConsolidatedRange = async () => {
    // Para rango de fechas, cargamos cada día y combinamos
    const start = new Date(selectedDate);
    const end = new Date(endDate);
    const allItems = {};
    
    let currentDate = start;
    while (currentDate <= end) {
      const dateStr = currentDate.toISOString().split('T')[0];
      try {
        const response = await ordersAPI.getConsolidated(dateStr);
        const filteredItems = filterBySelectedLocations(response.data.items);
        
        filteredItems.forEach(item => {
          if (!allItems[item.item_id]) {
            allItems[item.item_id] = {
              ...item,
              total_requested: 0,
              location_breakdown: {}
            };
          }
          allItems[item.item_id].total_requested += item.total_requested;
          
          // Combinar desglose por local
          Object.entries(item.location_breakdown).forEach(([loc, qty]) => {
            if (!allItems[item.item_id].location_breakdown[loc]) {
              allItems[item.item_id].location_breakdown[loc] = 0;
            }
            allItems[item.item_id].location_breakdown[loc] += qty;
          });
        });
      } catch (error) {
        // Continuar si no hay datos para esa fecha
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Recalcular pedido final (sin restar stock - stock es solo informativo)
    const consolidatedItems = Object.values(allItems).map(item => ({
      ...item,
      suggested_after_stock: Math.max(0, item.total_requested - item.warehouse_stock),
      final_required: item.total_requested  // Pedido final = total solicitado
    }));

    setConsolidated({
      order_date: `${selectedDate} a ${endDate}`,
      items: consolidatedItems,
      total_items: consolidatedItems.length,
      generated_at: new Date().toISOString()
    });
  };

  const filterBySelectedLocations = (items) => {
    const selectedLocationNames = locations
      .filter(loc => selectedLocations[loc.id])
      .map(loc => loc.name);

    return items.map(item => {
      const filteredBreakdown = {};
      let newTotal = 0;
      
      Object.entries(item.location_breakdown).forEach(([locName, qty]) => {
        if (selectedLocationNames.includes(locName)) {
          filteredBreakdown[locName] = qty;
          newTotal += qty;
        }
      });

      return {
        ...item,
        total_requested: newTotal,
        suggested_after_stock: Math.max(0, newTotal - item.warehouse_stock),
        final_required: newTotal,  // Pedido final = total solicitado (sin restar stock)
        location_breakdown: filteredBreakdown
      };
    }).filter(item => item.total_requested > 0);
  };

  const toggleLocation = (locationId) => {
    setSelectedLocations(prev => ({
      ...prev,
      [locationId]: !prev[locationId]
    }));
  };

  const selectAllLocations = () => {
    const all = {};
    locations.forEach(loc => {
      all[loc.id] = true;
    });
    setSelectedLocations(all);
  };

  const deselectAllLocations = () => {
    const none = {};
    locations.forEach(loc => {
      none[loc.id] = false;
    });
    setSelectedLocations(none);
  };

  // Extraer proveedores únicos
  const suppliers = useMemo(() => {
    if (!consolidated?.items) return [];
    const uniqueSuppliers = [...new Set(consolidated.items.map(item => item.supplier || 'Sin asignar'))];
    return uniqueSuppliers.sort();
  }, [consolidated]);

  // Filtrar items por proveedor
  const filteredItems = useMemo(() => {
    if (!consolidated?.items) return [];
    if (selectedSupplier === 'all') return consolidated.items;
    return consolidated.items.filter(item => 
      (item.supplier || 'Sin asignar') === selectedSupplier
    );
  }, [consolidated, selectedSupplier]);

  // Agrupar por proveedor
  const groupedBySupplier = useMemo(() => {
    const groups = {};
    filteredItems.forEach(item => {
      const supplier = item.supplier || 'Sin asignar';
      if (!groups[supplier]) groups[supplier] = [];
      groups[supplier].push(item);
    });
    return groups;
  }, [filteredItems]);

  const selectedLocationCount = Object.values(selectedLocations).filter(Boolean).length;

  // Función para copiar pedido de un proveedor a WhatsApp con detalle por local
  const copySupplierToWhatsApp = (supplier, items) => {
    const dateStr = useRange 
      ? `${selectedDate} al ${endDate}` 
      : selectedDate;
    
    let content = `*PEDIDO - ${supplier}*\n`;
    content += `Fecha: ${dateStr}\n\n`;
    
    items.forEach(item => {
      if (item.final_required > 0) {
        content += `*${item.item_name}*\n`;
        content += `  Total a pedir: *${item.final_required} ${item.unit}*\n`;
        content += `  Detalle por local:\n`;
        Object.entries(item.location_breakdown).forEach(([location, qty]) => {
          if (qty > 0) {
            content += `    • ${location}: ${qty} ${item.unit}\n`;
          }
        });
        content += `\n`;
      }
    });
    
    const totalItems = items.filter(i => i.final_required > 0).length;
    const totalUnits = items.reduce((sum, i) => sum + i.final_required, 0);
    content += `_Total: ${totalItems} productos, ${totalUnits} unidades_`;
    
    navigator.clipboard.writeText(content).then(() => {
      toast.success(`Pedido de ${supplier} copiado al portapapeles`);
    }).catch(() => {
      toast.error('Error al copiar');
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-consolidated-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Pedido Consolidado</h1>
          <p className="text-muted-foreground mt-1">Consolida pedidos por fecha, local y proveedor</p>
        </div>
      </div>

      {/* Alerta de locales sin bodega */}
      {localesSinBodega.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">
              {localesSinBodega.length} local(es) sin bodega asignada
            </p>
            <p className="text-sm text-amber-700 mt-1">
              Los siguientes locales no tienen bodega asignada y sus pedidos aparecerán en Bodega Central por defecto:
            </p>
            <p className="text-sm font-medium text-amber-800 mt-1">
              {localesSinBodega.map(l => l.name).join(', ')}
            </p>
            <p className="text-xs text-amber-600 mt-2">
              → Ve a Configuración → Locales para asignar una bodega a cada local.
            </p>
          </div>
        </div>
      )}

      {/* Filtros de fecha y locales */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-4">
        {/* Selector de fechas */}
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              {useRange ? 'Fecha Inicio' : 'Fecha'}
            </label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-44"
            />
          </div>
          
          {useRange && (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Fecha Fin</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-44"
                min={selectedDate}
              />
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Checkbox
              id="useRange"
              checked={useRange}
              onCheckedChange={setUseRange}
            />
            <label htmlFor="useRange" className="text-sm text-slate-700 cursor-pointer">
              Rango de fechas
            </label>
          </div>
        </div>

        {/* Selector de locales */}
        <div>
          <div 
            className="flex items-center justify-between cursor-pointer py-2"
            onClick={() => setShowOrderSelector(!showOrderSelector)}
          >
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">
                Seleccionar Locales ({selectedLocationCount} de {locations.length})
              </span>
            </div>
            {showOrderSelector ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          
          {showOrderSelector && (
            <div className="mt-2 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex gap-2 mb-3">
                <Button variant="outline" size="sm" onClick={selectAllLocations}>
                  <CheckSquare className="w-4 h-4 mr-1" /> Todos
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAllLocations}>
                  <Square className="w-4 h-4 mr-1" /> Ninguno
                </Button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {locations.map(location => {
                  const orderForLocation = ordersByDate.find(o => o.location_id === location.id);
                  const hasOrder = !!orderForLocation;
                  const isSubmitted = orderForLocation?.status === 'submitted';
                  
                  return (
                    <div 
                      key={location.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                        selectedLocations[location.id] 
                          ? 'border-primary bg-primary/5' 
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <Checkbox
                        id={`loc-${location.id}`}
                        checked={selectedLocations[location.id] || false}
                        onCheckedChange={() => toggleLocation(location.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <label 
                          htmlFor={`loc-${location.id}`}
                          className="text-sm font-medium text-slate-900 cursor-pointer block truncate"
                        >
                          {location.name}
                          {location.order_type === 'barra' && (
                            <span className="ml-1 text-xs text-purple-600">(Barra)</span>
                          )}
                        </label>
                        {!useRange && (
                          <span className={`text-xs ${
                            isSubmitted ? 'text-green-600' : 
                            hasOrder ? 'text-amber-600' : 'text-slate-400'
                          }`}>
                            {isSubmitted ? '✓ Pedido enviado' : 
                             hasOrder ? 'Borrador' : 'Sin pedido'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-4">
          <Button onClick={loadConsolidated} disabled={loading || selectedLocationCount === 0}>
            <TrendingUp className="w-4 h-4 mr-2" />
            Generar Consolidado
          </Button>
          
          {/* Filtro por tipo de pedido */}
          <Select value={selectedOrderType} onValueChange={setSelectedOrderType}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Tipo de pedido" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los pedidos</SelectItem>
              <SelectItem value="cocina">Solo Cocina</SelectItem>
              <SelectItem value="barra">Solo Barra</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : consolidated ? (
        <div className="space-y-6">
          {/* Header del consolidado */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-heading font-bold text-slate-900">
                    {typeof consolidated.order_date === 'string' && consolidated.order_date.includes(' a ') 
                      ? consolidated.order_date 
                      : formatDate(consolidated.order_date)}
                  </h2>
                  {consolidated.order_type && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      consolidated.order_type === 'barra' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {consolidated.order_type === 'barra' ? 'Pedidos Barra' : 'Pedidos Cocina'}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {consolidated.items.length} productos • {selectedLocationCount} local(es)
                </p>
              </div>
              
              {/* Filtro por proveedor */}
              <div className="w-full sm:w-64">
                <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                  <SelectTrigger>
                    <Filter className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Filtrar por proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los proveedores</SelectItem>
                    {suppliers.map(supplier => (
                      <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-sm text-slate-600">
                <span className="font-medium text-slate-700">El stock registrado en bodega es informativo y no reduce automáticamente el pedido final.</span>
              </p>
            </div>
          </div>

          {/* Tabla agrupada por proveedor */}
          {Object.entries(groupedBySupplier).map(([supplier, items]) => (
            <div key={supplier} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-slate-100 px-6 py-3 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">{supplier}</h3>
                  <p className="text-xs text-slate-500">
                    {items.length} productos • {items.filter(i => i.final_required > 0).length} a pedir
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copySupplierToWhatsApp(supplier, items)}
                  className="text-green-600 border-green-300 hover:bg-green-50"
                  disabled={items.filter(i => i.final_required > 0).length === 0}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Copiar WhatsApp
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Código</th>
                      <th className="px-4 py-3 text-left font-semibold">Producto</th>
                      <th className="px-4 py-3 text-right font-semibold">Solicitado por locales</th>
                      <th className="px-4 py-3 text-right font-semibold bg-slate-100">Stock registrado bodega</th>
                      <th className="px-4 py-3 text-right font-semibold bg-amber-50" title="Referencia calculada a partir del stock registrado en sistema">Sugerido considerando stock</th>
                      <th className="px-4 py-3 text-right font-semibold bg-green-50">Pedido final</th>
                      <th className="px-4 py-3 text-left font-semibold">Desglose por local</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.item_id} className="border-b hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-4 font-mono text-sm">{item.item_code}</td>
                        <td className="px-4 py-4">
                          <p className="font-semibold text-slate-900">{item.item_name}</p>
                        </td>
                        <td className="px-4 py-4 text-right font-semibold text-slate-900">
                          {item.total_requested} {item.unit}
                        </td>
                        <td className="px-4 py-4 text-right bg-slate-50 text-slate-600">
                          {item.warehouse_stock} {item.unit}
                        </td>
                        <td className="px-4 py-4 text-right bg-amber-50/50 text-amber-700">
                          {item.suggested_after_stock || Math.max(0, item.total_requested - item.warehouse_stock)} {item.unit}
                        </td>
                        <td className="px-4 py-4 text-right bg-green-50/50">
                          <span className="font-bold text-green-700">
                            {item.final_required} {item.unit}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            {Object.entries(item.location_breakdown).map(([location, qty]) => (
                              <div key={location} className="text-xs text-slate-600">
                                <span className="font-medium">{location}:</span> {qty} {item.unit}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Resumen */}
          <div className="bg-gradient-to-br from-primary to-accent text-white rounded-lg p-6 shadow-lg">
            <h3 className="font-heading font-bold text-lg mb-2">Resumen</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm opacity-90">Total Productos</p>
                <p className="text-2xl font-bold">{filteredItems.length}</p>
              </div>
              <div>
                <p className="text-sm opacity-90">Requieren Pedido</p>
                <p className="text-2xl font-bold">
                  {filteredItems.filter(i => i.final_required > 0).length}
                </p>
              </div>
              <div>
                <p className="text-sm opacity-90">Cubiertos por Stock</p>
                <p className="text-2xl font-bold">
                  {filteredItems.filter(i => i.final_required === 0).length}
                </p>
              </div>
              <div>
                <p className="text-sm opacity-90">Proveedores</p>
                <p className="text-2xl font-bold">{suppliers.length}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-muted-foreground">Selecciona fechas y locales para generar el consolidado</p>
        </div>
      )}
    </div>
  );
};

export default AdminConsolidatedPage;
